import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// Operator-only tenant targeting. Operators pick which tenant the user/group/
// role management calls act on; the backend pins tenant-admins regardless, so
// this is a no-op for them. Only the management endpoints carry the param.
let targetTenant: string | null = null;
export function setTargetTenant(slug: string | null): void {
  targetTenant = slug && slug.trim() ? slug.trim() : null;
}
export function getTargetTenant(): string | null {
  return targetTenant;
}
const MGMT_PREFIXES = ['/users', '/groups', '/roles', '/guests', '/risk', '/ca'];

// Produktiv authentisiert das oauth2-proxy-Cookie (HttpOnly) per
// ForwardAuth; die SPA haelt dann gar kein Token. Nur der Dev-Fallback
// (`?token=`, AuthContext) setzt eines -- und das liegt nur im Speicher,
// nie in Web Storage (docs/app-integration-oidc.md, "Tokens im Browser:
// Regeln"). Nach dem Neuladen ist es weg.
let devToken: string | null = null;
export function setDevToken(token: string | null): void {
  devToken = token;
}
// Altlast: frueher lag der Dev-Token unter `iga.token` im localStorage.
try {
  localStorage.removeItem('iga.token');
} catch {
  /* Storage gesperrt -- dann liegt dort auch nichts */
}

api.interceptors.request.use((config) => {
  const token = devToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const url = config.url ?? '';
  if (targetTenant && MGMT_PREFIXES.some((p) => url === p || url.startsWith(`${p}/`) || url.startsWith(`${p}?`))) {
    config.params = { ...(config.params as Record<string, unknown>), tenant: targetTenant };
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // The /me endpoint IS the identity probe — AuthContext renders the
    // sign-in screen on a 401 there, so don't reload (would loop).
    const url: string = err.config?.url ?? '';
    if (err.response?.status === 401 && !url.endsWith('/me')) {
      devToken = null;
      window.location.reload();
    }
    return Promise.reject(err);
  },
);

export default api;

// --- Identity -------------------------------------------------------
export interface Me {
  sub: string;
  username: string;
  email: string;
  roles: string[];
  tenant: string;
  capability: 'operator' | 'tenant-admin' | 'pim-user' | 'guest-sponsor';
}

// --- Access requests ------------------------------------------------
export interface RequestedAccess {
  roles?: string[];
  groups?: string[];
}

export interface SodViolation {
  policyId: string;
  policyName: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  conflictingHeld: string[];
}

export interface AccessRequest {
  id: string;
  subject_user_id: string;
  subject_username: string;
  requested: RequestedAccess;
  justification: string;
  status: 'pending' | 'approved' | 'rejected';
  sod_violations: SodViolation[];
  created_by_email: string;
  created_at: string;
  decided_by: string | null;
  decision_reason: string | null;
}

// --- PIM (Just-in-Time privileged access) ---------------------------
export interface PimEligibleRole {
  id: string;
  role_name: string;
  client_id: string;
  description: string;
  max_minutes: number;
  enabled: boolean;
}

export type PimStatus = 'pending' | 'active' | 'denied' | 'cancelled' | 'expired' | 'revoked' | 'failed';

export interface PimRequest {
  id: string;
  kc_user_id: string;
  username: string;
  requester_email: string;
  role_name: string;
  client_id: string;
  justification: string;
  duration_minutes: number;
  status: PimStatus;
  created_at: string;
  decided_email: string | null;
  decision_reason: string | null;
  activated_at: string | null;
  expires_at: string | null;
  ended_at: string | null;
  end_reason: string | null;
  last_error: string | null;
}

export const pim = {
  listEligible: () => api.get<PimEligibleRole[]>('/pim/eligible-roles'),
  upsertEligible: (body: { roleName: string; clientId?: string; description?: string; maxMinutes?: number; enabled?: boolean }) =>
    api.post<PimEligibleRole>('/pim/eligible-roles', body),
  deleteEligible: (id: string) => api.delete(`/pim/eligible-roles/${encodeURIComponent(id)}`),
  listRequests: (params: { status?: PimStatus; mine?: boolean } = {}) =>
    api.get<PimRequest[]>('/pim/requests', {
      params: { status: params.status, mine: params.mine ? 'true' : undefined },
    }),
  request: (body: { roleName: string; clientId?: string; durationMinutes: number; justification: string }) =>
    api.post<PimRequest>('/pim/requests', body),
  approve: (id: string) => api.post<PimRequest>(`/pim/requests/${encodeURIComponent(id)}/approve`, {}),
  deny: (id: string, reason: string) =>
    api.post<PimRequest>(`/pim/requests/${encodeURIComponent(id)}/deny`, { reason }),
  cancel: (id: string) => api.post<PimRequest>(`/pim/requests/${encodeURIComponent(id)}/cancel`, {}),
  revoke: (id: string) => api.post<PimRequest>(`/pim/requests/${encodeURIComponent(id)}/revoke`, {}),
};

// --- Access reviews -------------------------------------------------
export interface ReviewCampaign {
  id: string;
  name: string;
  status: 'open' | 'completed' | 'cancelled';
  due_at: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface ReviewItem {
  id: string;
  campaign_id: string;
  kc_user_id: string;
  username: string;
  kind: 'role' | 'group';
  name: string;
  decision: 'pending' | 'certify' | 'revoke';
  reviewer_sub: string | null;
  comment: string | null;
}

// --- Attestations ---------------------------------------------------
export interface AttestationSnapshotEntry {
  kind: 'role' | 'group';
  name: string;
  service: string;
  source: string;
}

export interface Attestation {
  id: string;
  kc_user_id: string;
  username: string;
  manager_sub: string | null;
  status: 'pending' | 'attested' | 'flagged';
  snapshot: AttestationSnapshotEntry[];
  comment: string | null;
  created_at: string;
  decided_at: string | null;
}

// --- SoD policies ---------------------------------------------------
export interface SodPolicy {
  id: string;
  name: string;
  description: string;
  conflicting: string[];
  severity: 'low' | 'medium' | 'high' | 'critical';
  enabled: boolean;
}

// --- Audit ----------------------------------------------------------
export interface AuditRow {
  id: string;
  actor_sub: string;
  actor_email: string;
  action: string;
  target_user_id: string | null;
  payload: unknown;
  created_at: string;
}

// --- User / group / role management ---------------------------------
export interface KcUser {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
}

export interface KcUserDetail extends KcUser {
  emailVerified?: boolean;
  attributes?: Record<string, string[]>;
  createdTimestamp?: number;
}

export interface KcRole {
  id: string;
  name: string;
  description?: string;
}

export interface KcGroup {
  id: string;
  name: string;
  path: string;
}

export interface ActiveEntitlement {
  kind: 'role' | 'group';
  name: string;
  service: string;
  source: string;
}

export interface UserDetail {
  user: KcUserDetail;
  roles: KcRole[];
  groups: KcGroup[];
  entitlements: ActiveEntitlement[];
}

export interface MoverResult {
  sodViolations: SodViolation[];
}

export const mgmt = {
  // Users
  listUsers: (params: { search?: string; first?: number; max?: number }) =>
    api.get<{ users: KcUser[]; total: number; first: number; max: number }>('/users', { params }),
  getUser: (id: string) => api.get<UserDetail>(`/users/${encodeURIComponent(id)}`),
  createUser: (body: {
    username: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    sendInvite?: boolean;
    temporaryPassword?: string;
    roles?: string[];
    groups?: string[];
    force?: boolean;
  }) => api.post<{ userId: string; username: string; sodViolations: SodViolation[] }>('/users', body),
  updateUser: (id: string, body: { email?: string; firstName?: string; lastName?: string }) =>
    api.patch(`/users/${encodeURIComponent(id)}`, body),
  setUserEnabled: (id: string, enabled: boolean) =>
    api.post(`/users/${encodeURIComponent(id)}/${enabled ? 'enable' : 'disable'}`),
  setUserRoles: (id: string, body: { addRoles?: string[]; removeRoles?: string[]; force?: boolean }) =>
    api.post<MoverResult>(`/users/${encodeURIComponent(id)}/roles`, body),
  setUserGroups: (id: string, body: { addGroups?: string[]; removeGroups?: string[] }) =>
    api.post<MoverResult>(`/users/${encodeURIComponent(id)}/groups`, body),

  // Groups
  listGroups: (search?: string) =>
    api.get<{ groups: KcGroup[] }>('/groups', { params: search ? { search } : undefined }),
  getGroupChildren: (id: string) =>
    api.get<{ children: KcGroup[] }>(`/groups/${encodeURIComponent(id)}/children`),
  getGroupMembers: (id: string, first = 0, max = 100) =>
    api.get<{ members: KcUser[]; first: number; max: number }>(
      `/groups/${encodeURIComponent(id)}/members`,
      { params: { first, max } }
    ),
  createGroup: (body: { name: string; parentId?: string }) =>
    api.post<{ id: string; name: string }>('/groups', body),
  deleteGroup: (id: string) => api.delete(`/groups/${encodeURIComponent(id)}`),
  addGroupMember: (groupId: string, userId: string) =>
    api.post(`/groups/${encodeURIComponent(groupId)}/members`, { userId }),
  removeGroupMember: (groupId: string, userId: string) =>
    api.delete(`/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`),

  // Roles + composites (permission bundles)
  listRoles: (search?: string) =>
    api.get<{ roles: KcRole[] }>('/roles', { params: search ? { search } : undefined }),
  getRole: (name: string) =>
    api.get<{ role: KcRole; composites: KcRole[] }>(`/roles/${encodeURIComponent(name)}`),
  createRole: (body: { name: string; description?: string }) => api.post('/roles', body),
  addComposites: (name: string, roles: string[]) =>
    api.post(`/roles/${encodeURIComponent(name)}/composites`, { roles }),
  removeComposites: (name: string, roles: string[]) =>
    api.delete(`/roles/${encodeURIComponent(name)}/composites`, { data: { roles } }),
};

// --- Gastzugaenge (B2B) ----------------------------------------------
export type GuestStatus = 'active' | 'expired' | 'revoked';

export interface Guest {
  id: string;
  tenant_id: string;
  kc_user_id: string;
  username: string;
  email: string;
  display_name: string;
  organisation: string;
  purpose: string;
  sponsor_sub: string;
  sponsor_email: string;
  status: GuestStatus;
  invite_sent: boolean;
  invited_at: string;
  expires_at: string;
  extend_count: number;
  ended_at: string | null;
  ended_by: string | null;
  end_reason: string | null;
  last_error: string | null;
}

export interface GuestList {
  items: Guest[];
  maxDays: number;
  group: string;
  requestTtlDays?: number;
}

// Genehmigungsworkflow: Sponsor beantragt, Tenant-Admin/iga-admin entscheidet.
export type GuestRequestStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'expired';

export interface GuestRequest {
  id: string;
  tenant_id: string;
  email: string;
  first_name: string;
  last_name: string;
  organisation: string;
  purpose: string;
  days: number;
  requester_sub: string;
  requester_email: string;
  status: GuestRequestStatus;
  created_at: string;
  decided_by: string | null;
  decided_at: string | null;
  decision_reason: string | null;
  guest_id: string | null;
  last_error: string | null;
}

export interface GuestRequestList {
  items: GuestRequest[];
  ttlDays: number;
}

export interface CreateGuestRequestBody {
  email: string;
  firstName?: string;
  lastName?: string;
  organisation?: string;
  purpose: string;
  days: number;
}

export interface InviteGuestBody {
  email: string;
  firstName?: string;
  lastName?: string;
  organisation?: string;
  purpose: string;
  days?: number;
  expiresAt?: string;
  temporaryPassword?: string;
}

// --- Anmelde-Risiko (Identity Protection) ----------------------------
export type RiskLevel = 'medium' | 'high';
export type RiskStatus = 'open' | 'resolved' | 'false_positive';
export type RiskAction = 'logout' | 'require-mfa' | 'disable';

export interface RiskReason {
  code: string;
  weight: number;
  detail: string;
}

export interface RiskEvent {
  id: string;
  tenant_id: string;
  login_event_id: string;
  user_id: string;
  username: string | null;
  event_time: string;
  ip: string | null;
  country: string | null;
  city: string | null;
  client_id: string | null;
  score: number;
  level: RiskLevel;
  reasons: RiskReason[];
  status: RiskStatus;
  auto_action: string | null;
  actions: Array<{ action: RiskAction; by: string; at: string; ok: boolean; error?: string }>;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  created_at: string;
}

export interface RiskConfig {
  thresholds: { medium: number; high: number };
  learningLogins: number;
  trustedRanges: string[];
  autoAction: RiskAction | null;
  geoip: boolean;
}

export interface RiskList {
  items: RiskEvent[];
  config: RiskConfig;
}

export interface RiskPollResult {
  tenant: string;
  fetched: number;
  inserted: number;
  logins: number;
  findings: number;
  autoActions: number;
  errors: string[];
}

export const risk = {
  list: (params?: { status?: RiskStatus; level?: RiskLevel; userId?: string }) =>
    api.get<RiskList>('/risk/events', { params }),
  get: (id: string) => api.get<RiskEvent>(`/risk/events/${encodeURIComponent(id)}`),
  resolve: (id: string, body: { falsePositive?: boolean; note?: string }) =>
    api.post<RiskEvent>(`/risk/events/${encodeURIComponent(id)}/resolve`, body),
  respond: (id: string, action: RiskAction) =>
    api.post<RiskEvent>(`/risk/events/${encodeURIComponent(id)}/respond`, { action }),
  user: (userId: string) => api.get(`/risk/users/${encodeURIComponent(userId)}`),
  poll: () => api.post<RiskPollResult>('/risk/poll', {}),
};

// --- Bedingter Zugriff (Conditional Access, P2.2) ---------------------------

export type CaAction = 'deny' | 'mfa';
export type CaDecisionValue = 'allow' | 'mfa' | 'deny';
export type CaMode = 'enforce' | 'report';

export interface CaConditions {
  users?: { groups?: string[]; roles?: string[]; excludeGroups?: string[]; excludeRoles?: string[]; excludeUsers?: string[] };
  clients?: string[];
  locations?: { countries?: string[]; notCountries?: string[]; untrustedOnly?: boolean };
  risk?: { minLevel: 'medium' | 'high' };
  /** source fehlt = 'user' (Person hat ein konformes Geraet); 'login' = Geraetezertifikat der Anmeldung. */
  device?: { notCompliant: true; includeUnknown?: boolean; source?: CaDeviceSource };
  /** Anmelde-IP ist Tor-Ausgangsknoten, offener Proxy bzw. VPN-Anbieter; includeUnknown = auch ohne geladene Liste. */
  anonymizer?: { kinds: CaAnonymizerKind[]; includeUnknown?: boolean };
}

export type CaAnonymizerKind = 'tor' | 'proxy' | 'vpn';
/** 'unknown' = Liste nicht geladen oder keine Richtlinie nutzt die Bedingung. */
export type CaAnonymizerState = CaAnonymizerKind | 'none' | 'unknown';

export type CaDeviceState = 'compliant' | 'noncompliant' | 'unknown';
export type CaDeviceSource = 'user' | 'login';
/** Geraet der aktuellen Anmeldung; 'none' = kein Geraetezertifikat vorgelegt. */
export type CaLoginDeviceState = 'compliant' | 'noncompliant' | 'none' | 'unknown';

/** Geraetebindung der SSO-Sitzung (dbsc-gateway, ADR 0035), schwaechste zuerst. */
export type CaSessionLevel = 'off' | 'report' | 'enforce' | 'require';

/** dbsc fehlt = keine Sitzungssteuerung. */
export interface CaSessionControls {
  dbsc?: Exclude<CaSessionLevel, 'off'>;
}

export interface CaPolicy {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  mode: CaMode;
  /** null = reine Sitzungsrichtlinie (wirkt nicht auf die Anmeldung). */
  action: CaAction | null;
  priority: number;
  conditions: CaConditions;
  sessionControls: CaSessionControls;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CaPolicyInput = Pick<
  CaPolicy, 'name' | 'description' | 'enabled' | 'mode' | 'action' | 'priority' | 'conditions' | 'sessionControls'
>;

export interface CaMatch {
  id: string;
  name: string;
  mode: CaMode;
  action: CaAction;
  reasons: string[];
}

export interface CaDecisionRow {
  id: string;
  decided_at: string;
  user_id: string;
  username: string | null;
  ip: string | null;
  country: string | null;
  client_id: string | null;
  decision: CaDecisionValue;
  report_decision: CaDecisionValue;
  risk_level: string;
  device_state?: CaDeviceState;
  login_device_state?: CaLoginDeviceState;
  anonymizer?: CaAnonymizerState;
  matched: CaMatch[];
}

export interface CaWhatIfInput {
  username?: string;
  userId?: string;
  groups?: string[];
  roles?: string[];
  clientId?: string;
  ip?: string;
  country?: string;
  trusted?: boolean;
  riskLevel?: 'none' | 'medium' | 'high';
  deviceState?: CaDeviceState;
  loginDeviceState?: CaLoginDeviceState;
  anonymizer?: CaAnonymizerState;
  clientCertSha256?: string;
}

export interface CaWhatIfResult {
  decision: CaDecisionValue;
  reportDecision: CaDecisionValue;
  matched: CaMatch[];
  reasons: string[];
  country: string | null;
  trusted: boolean;
  riskLevel: 'none' | 'medium' | 'high';
  deviceState?: CaDeviceState;
  loginDeviceState?: CaLoginDeviceState;
  anonymizer?: CaAnonymizerState;
  session?: {
    dbsc: CaSessionLevel;
    reportDbsc: CaSessionLevel;
    matched: Array<{ id: string; name: string; mode: CaMode; dbsc: Exclude<CaSessionLevel, 'off'>; reasons: string[] }>;
  };
}

export interface CaAnonymizerListStatus {
  configured: boolean;
  lists: Array<{ kind: CaAnonymizerKind; loadedAt: string | null; ranges: number; sources: number }>;
}

export const ca = {
  policies: () => api.get<{ items: CaPolicy[] }>('/ca/policies'),
  create: (body: CaPolicyInput) => api.post<CaPolicy>('/ca/policies', body),
  update: (id: string, body: Partial<CaPolicyInput>) => api.patch<CaPolicy>(`/ca/policies/${encodeURIComponent(id)}`, body),
  remove: (id: string) => api.delete(`/ca/policies/${encodeURIComponent(id)}`),
  decisions: (params?: { decision?: CaDecisionValue; userId?: string; limit?: number }) =>
    api.get<{ items: CaDecisionRow[] }>('/ca/decisions', { params }),
  whatIf: (body: CaWhatIfInput) => api.post<CaWhatIfResult>('/ca/what-if', body),
  anonymizerLists: () => api.get<CaAnonymizerListStatus>('/ca/anonymizer-lists'),
};

export const guests = {
  list: (params?: { status?: GuestStatus; mine?: boolean }) =>
    api.get<GuestList>('/guests', { params: { status: params?.status, mine: params?.mine ? 'true' : undefined } }),
  invite: (body: InviteGuestBody) => api.post<Guest>('/guests', body),
  extend: (id: string, body: { days?: number; expiresAt?: string }) =>
    api.post<Guest>(`/guests/${encodeURIComponent(id)}/extend`, body),
  revoke: (id: string, reason?: string) =>
    api.post<Guest>(`/guests/${encodeURIComponent(id)}/revoke`, { reason }),
  requests: {
    list: (params?: { status?: GuestRequestStatus; mine?: boolean }) =>
      api.get<GuestRequestList>('/guests/requests', {
        params: { status: params?.status, mine: params?.mine ? 'true' : undefined },
      }),
    create: (body: CreateGuestRequestBody) => api.post<GuestRequest>('/guests/requests', body),
    approve: (id: string, body: { days?: number; expiresAt?: string } = {}) =>
      api.post<GuestRequest>(`/guests/requests/${encodeURIComponent(id)}/approve`, body),
    reject: (id: string, reason: string) =>
      api.post<GuestRequest>(`/guests/requests/${encodeURIComponent(id)}/reject`, { reason }),
    withdraw: (id: string) => api.post<GuestRequest>(`/guests/requests/${encodeURIComponent(id)}/withdraw`, {}),
  },
};
